/**
 * Cosmetic skins.
 *
 * In `shared/` rather than in the backend because three parties must agree on what a skin id
 * means: the meta tier that grants and equips it, the realtime tier that tells everyone in the
 * room which one you are wearing, and the client that draws it. A catalogue defined on one
 * side and duplicated on the others is a catalogue that drifts.
 *
 * ## What a skin owns
 *
 * **A skin owns how a body looks. It never owns how a team reads.**
 *
 * Those were the same sentence until 2026-09-08, when body and head took `TEAM_COLOUR` for
 * every skin and the playtest verdict on a paid cosmetic was "a red mannequin". The team signal
 * moved to an inverted-hull outline around the silhouette (`frontend/src/engine/arena/
 * team-outline.ts`) and the body kept its authored colours — which separates the two concerns
 * for the first time: the outline is the anti-impersonation guarantee, and everything inside it
 * is the thing a player bought. Nothing below may touch the outline; that colour comes from the
 * team, on the server's word, and no catalogue field reaches it.
 *
 * So a skin owns three things, in ascending order of how much they change the silhouette:
 *
 *   - `accent` — the visor and chest stripe. The only thing a skin owned before this file's
 *     first rewrite, and still the only thing that shows on a rig with a trim material to spare.
 *   - `bodyColour` — the dye on the body's own materials. New on 2026-09-10.
 *   - `model` — a different rig entirely.
 *
 * ## Why `bodyColour` is not the thing the 2026-09-08 decision forbade
 *
 * It looks like it should be. It is the opposite: the decision protects a body that carries its
 * own colour, because carrying its own colour is what a skin is for. What it forbids is *the
 * team* choosing that colour, and no team hex is reachable from here.
 *
 * Two rules on the value, both about legibility rather than taste:
 *
 *   - **Never near a team hue.** A body in `TEAM_COLOUR`'s deep orange or cyan would put the
 *     wrong answer inside the right outline, which is the confusion the outline exists to end.
 *     The same reason `accent` is never one of those two, one layer further in.
 *   - **A near-neutral wash on a rig whose one material covers the whole figure.** Every
 *     POLYGON body is a single `lambert2` over the pack's master atlas — face, hands and hair
 *     included — so the dye multiplies over skin tone too. A pale or dusty multiplier reads as
 *     the same operator somewhere colder or dirtier; a saturated one reads as a mannequin, which
 *     is the note the tint decision was overturned on. Full hues are for the rigs that keep
 *     their skin and armour on separate materials (`swat`, `hoodie`, the three `relay-*` kits),
 *     where the dye lands on fabric and nothing else.
 *
 * ## Why ids are numbers
 *
 * They go on the wire (`MSG.PLAYER_INFO`, one byte). A string id would cost the length of the
 * name every time somebody joins, for a value from a fixed list both sides already hold.
 */

/** Ids are stable and must never be reused; the wire and the database both store the number. */
export const SKIN = {
  /** What everyone starts with. Must be id 0: an unknown skin falls back to it. */
  standard: 0,
  veteran: 1,
  circuit: 2,
  hazard: 3,
  monochrome: 4,
  matt: 5,
  sam: 6,
  shaun: 7,
  lis: 8,
  vanguard: 9,
  outlaw: 10,
  sentinel: 11,
  raider: 12,
  disco: 13,
  suit: 14,
  wraith: 15,
  sprinter: 16,
  striker: 17,
  pacer: 18,
  runner: 19,
  jogger: 20,
  commando: 21,
  // ── 2026-09-09: three material variants of our own `swat` rig (byte-identical skeleton and
  // the same 24 tactical clips), so new kit costs no new rigging. Ids 22–24; 1–8 stay retired
  // and are never reused. The key is `hazmat` and not `hazard` because retired id 3 still owns
  // that word here — the name a player reads is 'Hazard'; the key is only ours.
  polar: 22,
  signal: 23,
  hazmat: 24,
  // ── 2026-09-10: sixteen more operators (ids 25–40). Two claim rigs the registry had but no
  // skin equipped (`swat`, `hoodie`); the rest are colourways, which only became possible once
  // the team mark moved from a body tint to an outline (2026-09-09). Ids 1–8 stay retired.
  breacher: 25,
  nightwatch: 26,
  dunes: 27,
  moss: 28,
  drifter: 29,
  roadie: 30,
  ember: 31,
  midnight: 32,
  beacon: 33,
  timber: 34,
  sirocco: 36,
  ashfall: 37,
  tarpit: 38,
  nightshift: 39,
  awaykit: 40,
} as const;

export type SkinId = (typeof SKIN)[keyof typeof SKIN];

/** How a skin is earned. Checked by the meta tier, which is the only side that knows. */
export type SkinUnlock =
  | { kind: 'default' }
  /** Account level, from XP — or the gem price, for a player who would rather pay than grind. */
  | { kind: 'level'; level: number; gemCost: number }
  /** Competitive rank points in the current season — or the gem price. */
  /** Kept in the union for the wire and the switch statements; no skin uses it since 2026-09-11
   * (the rank-point gates on skins were removed) — every former rank skin is a plain purchase. */
  | { kind: 'rank'; points: number; gemCost: number }
  /** Bought with Gems (the PREMIUM currency). Credits buy nothing here since 2026-09-09. */
  | { kind: 'purchase'; gemCost: number };

export interface SkinDefinition {
  id: SkinId;
  name: string;
  /** One line, shown in the equip screen. Says how it looks, not how it was made. */
  description: string;
  /**
   * The accent colour, applied to the visor and chest stripe.
   *
   * Chosen to read against *both* team colours, which is why none of them is orange or cyan: an
   * accent in a team hue would be a team marker a player picked for themselves.
   *
   * Set for every skin, including the ones whose rig has no trim material left to paint (most
   * of the POLYGON bodies — see `character.ts`'s `CHARACTER_MODEL_SPECS`). It still matters
   * there: the equip screen draws this as the row's swatch and does no 3D rendering at all, so
   * this is the only colour a player sees before they own the thing.
   */
  accent: number;
  /**
   * The body's own dye, multiplied over the rig's authored materials — the fabric, never the
   * outline, and never the face on a rig that keeps one.
   *
   * Omitted means "as the artist authored it", which is what most entries want and what the
   * base kit of each rig is. Present, it turns one rig into another operator: `character.ts`
   * captures each body material's authored colour when it clones it, so swapping between two
   * colourways of the same rig restores rather than accumulates.
   *
   * Both constraints on the value are in this file's header, and one of them is not a
   * preference: a near-neutral wash on a whole-figure atlas, full hues only where the dye can
   * land on fabric alone.
   */
  bodyColour?: number;
  /**
   * Names a character family in `frontend/src/engine/arena/character.ts`'s model registry
   * (`'matt'`, `'sam'`, `'shaun'`, `'lis'` today) instead of the wearer's team default rig.
   * Omitted for a skin that dresses the wearer's team default rig instead of bringing its own.
   * Two entries may name the same rig and differ only in `bodyColour` — that is the whole point
   * of the 2026-09-10 drop, and `character.ts` handles it without loading the GLB twice.
   */
  model?: string;
  unlock: SkinUnlock;
}

export const SKINS: Record<number, SkinDefinition> = {
  [SKIN.standard]: {
    id: SKIN.standard,
    name: 'Standard Issue',
    description: 'Regular kit. What everyone starts in.',
    accent: 0xe2e8f0,
    unlock: { kind: 'default' },
  },
  // The catalogue is POLYGON-only since Sprint 06's visual rework. Ids 1–8 (the trim skins
  // and the Quaternius survivor models) are RETIRED, never reused: the trims tinted an accent
  // material the single-material Synty rigs do not have, and the survivor bodies were the
  // mismatched old look the playtest called out. A retired id arriving from the wire or a
  // database row falls back to Standard Issue via `skinById`, by that function's own contract.
  [SKIN.vanguard]: {
    id: SKIN.vanguard,
    name: 'Vanguard',
    description: 'Front-line plates, squared away, nothing loose to catch.',
    accent: 0x94a3b8,
    model: 'synty-vanguard',
    unlock: { kind: 'purchase', gemCost: 2500 },
  },
  [SKIN.outlaw]: {
    id: SKIN.outlaw,
    name: 'Outlaw',
    description: 'Field jacket stripped of insignia. Never signed anything, never will.',
    // Regraded 2026-09-10: this was deep orange, within a few steps of `TEAM_COLOUR[RED]`
    // (#ea580c) — an accent the colour of the other team's body signal, which is the exact
    // confusion the rule at the top of this file exists to prevent. Measured RGB distance to
    // the two team hexes went from (147, 254) to (244, 184).
    accent: 0xa855f7,
    model: 'synty-outlaw',
    unlock: { kind: 'purchase', gemCost: 2750 },
  },
  [SKIN.sentinel]: {
    id: SKIN.sentinel,
    name: 'Sentinel',
    description: 'Full ghillie, hood and netting. Reads as shrubbery until it moves.',
    accent: 0x4ade80,
    model: 'synty-sentinel',
    unlock: { kind: 'purchase', gemCost: 4500 },
  },
  [SKIN.raider]: {
    id: SKIN.raider,
    name: 'Raider',
    description: 'Patched denim and every scar earned the long way. Level 30.',
    accent: 0xb45309,
    model: 'synty-raider',
    unlock: { kind: 'level', level: 30, gemCost: 4500 },
  },
  // ── The 2026-09-03 drop: every remaining body in the licensed pack (nine of them), so the
  // shop is a wall instead of a shelf. Two are level unlocks and one is a rank flex, so
  // progression keeps paying. Accents are swatch-only on these single-material rigs (there is
  // no trim material to paint), chosen clear of both team hues as always.
  [SKIN.disco]: {
    id: SKIN.disco,
    name: 'Disco',
    description: 'Straight out of another decade, platforms included.',
    accent: 0xf59e0b,
    model: 'synty-disco',
    unlock: { kind: 'purchase', gemCost: 2000 },
  },
  [SKIN.suit]: {
    id: SKIN.suit,
    name: 'Suit',
    description: 'Business on the clock, and the tie stays knotted.',
    accent: 0x94a3b8,
    model: 'synty-suit',
    unlock: { kind: 'purchase', gemCost: 3000 },
  },
  [SKIN.wraith]: {
    id: SKIN.wraith,
    name: 'Wraith',
    description: 'Black on black, hood to boot, matte all the way through.',
    accent: 0x7c6fee,
    model: 'synty-wraith',
    unlock: { kind: 'purchase', gemCost: 3250 },
  },
  [SKIN.sprinter]: {
    id: SKIN.sprinter,
    name: 'Sprinter',
    description: 'Built for the long straights. Travels light and shows it.',
    accent: 0xf472b6,
    model: 'synty-sprinter',
    unlock: { kind: 'purchase', gemCost: 2250 },
  },
  [SKIN.striker]: {
    id: SKIN.striker,
    name: 'Striker',
    description: 'Matchday kit, wrong sport entirely.',
    accent: 0xfacc15,
    model: 'synty-striker',
    unlock: { kind: 'purchase', gemCost: 1750 },
  },
  [SKIN.pacer]: {
    id: SKIN.pacer,
    name: 'Pacer',
    description: 'Track top, no breath wasted. Level 12.',
    accent: 0xa3e635,
    model: 'synty-pacer',
    unlock: { kind: 'level', level: 12, gemCost: 2500 },
  },
  [SKIN.runner]: {
    id: SKIN.runner,
    name: 'Runner',
    description: 'The cheapest way to look faster than you are.',
    accent: 0x84cc16,
    model: 'synty-runner',
    unlock: { kind: 'purchase', gemCost: 1500 },
  },
  [SKIN.jogger]: {
    id: SKIN.jogger,
    name: 'Jogger',
    description: 'Steady pace, zone two, all afternoon. Level 20.',
    accent: 0xd8b4fe,
    model: 'synty-jogger',
    unlock: { kind: 'level', level: 20, gemCost: 3500 },
  },
  [SKIN.commando]: {
    id: SKIN.commando,
    name: 'Commando',
    description: 'No armour at all. The flex is the armour.',
    accent: 0xe2e8f0,
    model: 'synty-commando',
    unlock: { kind: 'purchase', gemCost: 3500 },
  },
  // ── The 2026-09-09 drop: three kits built as material variants of our own `swat` rig, not
  // new bodies — same meshes, one skeleton, the same 24 clips, so they animate identically and
  // cost a texture set each. Uniform and team stripe are team-tinted like every other body;
  // the armour plate and the exposed skin keep the designer's colours, which is what makes the
  // three read as different kit instead of the same figure in three hues. Accents are the
  // visor, as the trim-only rule intends.
  [SKIN.polar]: {
    id: SKIN.polar,
    name: 'Polar',
    description: 'Arctic white over grey plate, ice-blue visor.',
    accent: 0x7de0ff,
    model: 'relay-polar',
    unlock: { kind: 'purchase', gemCost: 2750 },
  },
  [SKIN.signal]: {
    id: SKIN.signal,
    name: 'Signal',
    description: 'Signals-corps slate, teal visor.',
    accent: 0x5eead4,
    model: 'relay-signal',
    unlock: { kind: 'purchase', gemCost: 6000 },
  },
  [SKIN.hazmat]: {
    id: SKIN.hazmat,
    name: 'Hazard',
    description: 'Desert sand kit, orange visor, sun-bleached webbing. Level 15.',
    // Regraded 2026-09-10: this was deep orange, within a few steps of `TEAM_COLOUR[RED]`
    // (#ea580c) — an accent the colour of the other team's body signal, which is the exact
    // confusion the rule at the top of this file exists to prevent. Measured RGB distance to
    // the two team hexes went from (114, 268) to (154, 299).
    accent: 0xd4f000,
    model: 'relay-hazard',
    unlock: { kind: 'level', level: 15, gemCost: 3000 },
  },
  // ── The 2026-09-10 drop: sixteen operators, and the first ones in the catalogue that carry a
  // colour of their own (`bodyColour`). Two claim rigs nothing equipped — `swat` and `hoodie`,
  // the pre-rework team defaults, both of which ship a real trim material so their accent
  // renders in the match and not only in the swatch. The other fourteen are colourways, which
  // the 2026-09-09 outline decision made possible: the body is no longer overwritten, so a kit
  // in another dye is a different operator on the same skeleton at no rigging cost.
  [SKIN.breacher]: {
    id: SKIN.breacher,
    name: 'Breacher',
    description: 'Full assault shell, black webbing over grey plate, amber visor.',
    accent: 0xfacc15,
    model: 'swat',
    unlock: { kind: 'purchase', gemCost: 3500 },
  },
  [SKIN.nightwatch]: {
    id: SKIN.nightwatch,
    name: 'Nightwatch',
    description: 'Deep navy shell that reads black in cover, rose visor.',
    accent: 0xe11d48,
    model: 'swat',
    bodyColour: 0x1b2340,
    unlock: { kind: 'purchase', gemCost: 3000 },
  },
  [SKIN.dunes]: {
    id: SKIN.dunes,
    name: 'Dunes',
    description: 'Sand shell, pale gold visor, grey plate underneath. Level 18.',
    accent: 0xfde68a,
    model: 'swat',
    bodyColour: 0xcaa46a,
    unlock: { kind: 'level', level: 18, gemCost: 3000 },
  },
  [SKIN.moss]: {
    id: SKIN.moss,
    name: 'Moss',
    description: 'Olive drab from collar to boot, lime visor.',
    accent: 0xa3e635,
    model: 'swat',
    bodyColour: 0x46543a,
    unlock: { kind: 'purchase', gemCost: 2750 },
  },
  [SKIN.drifter]: {
    id: SKIN.drifter,
    name: 'Drifter',
    description: 'Hood up, sleeves down, nothing issued by anybody. Indigo cuffs.',
    accent: 0x818cf8,
    model: 'hoodie',
    unlock: { kind: 'purchase', gemCost: 3250 },
  },
  [SKIN.roadie]: {
    id: SKIN.roadie,
    name: 'Roadie',
    description: 'Black hood, black jeans, fuchsia trim. Dressed for the load-out door.',
    accent: 0xe879f9,
    model: 'hoodie',
    bodyColour: 0x2a2a30,
    unlock: { kind: 'purchase', gemCost: 2000 },
  },
  [SKIN.ember]: {
    id: SKIN.ember,
    name: 'Ember',
    description: 'Deep crimson kit over grey plate, rose visor.',
    accent: 0xfda4af,
    model: 'relay-polar',
    bodyColour: 0x8c1c1c,
    unlock: { kind: 'purchase', gemCost: 2500 },
  },
  [SKIN.midnight]: {
    id: SKIN.midnight,
    name: 'Midnight',
    description: 'Navy so dark it passes for black, pale blue visor. Level 24.',
    accent: 0x93c5fd,
    model: 'relay-polar',
    bodyColour: 0x151b2e,
    unlock: { kind: 'level', level: 24, gemCost: 2750 },
  },
  [SKIN.beacon]: {
    id: SKIN.beacon,
    name: 'Beacon',
    description: 'Acid-yellow suit, sand plate, no interest in cover.',
    accent: 0xfef08a,
    model: 'relay-hazard',
    bodyColour: 0xd6d017,
    unlock: { kind: 'purchase', gemCost: 3250 },
  },
  [SKIN.timber]: {
    id: SKIN.timber,
    name: 'Timber',
    description: 'Forest-green field kit over slate plate, mint visor.',
    accent: 0x86efac,
    model: 'relay-signal',
    bodyColour: 0x3c5238,
    unlock: { kind: 'purchase', gemCost: 3000 },
  },
  // The washes: near-neutral, because these rigs wear one atlas over the whole figure, face
  // included — see the `bodyColour` field's own note. Each one reads as the same body somewhere
  // colder, dustier or dirtier, which is what a wash can honestly claim to be.
  // NO DYED GHILLIE, and this is a wall rather than an oversight. `synty-sentinel`'s atlas is
  // very nearly black, and a `bodyColour` MULTIPLIES that atlas — so every dye lands on it
  // invisibly, pale or dark alike. Tried twice on 2026-09-10: a near-white wash rendered pure
  // black, and a cold near-black rendered indistinguishable from Sentinel, the undyed rig. A
  // 1750-gem skin that looks exactly like one the player may already own is worse than no skin,
  // so the entry was removed rather than shipped. Same wall `weapon-skins.ts` records for
  // Arctic; the way past it there was an emissive lift, which this rig's single `lambert2` has
  // nowhere to put.
  [SKIN.sirocco]: {
    id: SKIN.sirocco,
    name: 'Sirocco',
    description: 'Merc kit sand-blasted pale, everything the colour of the road. Level 8.',
    accent: 0xfcd34d,
    model: 'synty-outlaw',
    bodyColour: 0xdcc3a0,
    unlock: { kind: 'level', level: 8, gemCost: 1500 },
  },
  [SKIN.ashfall]: {
    id: SKIN.ashfall,
    name: 'Ashfall',
    description: 'Fatigues under a fall of grey ash, cold light on the plates.',
    accent: 0xcbd5e1,
    model: 'synty-vanguard',
    bodyColour: 0xa9b0bd,
    unlock: { kind: 'purchase', gemCost: 1500 },
  },
  [SKIN.tarpit]: {
    id: SKIN.tarpit,
    name: 'Tarpit',
    description: 'Oil-stained coveralls, grease to the elbows.',
    accent: 0xa8a29e,
    model: 'synty-raider',
    bodyColour: 0x8a8268,
    unlock: { kind: 'purchase', gemCost: 1250 },
  },
  [SKIN.nightshift]: {
    id: SKIN.nightshift,
    name: 'Nightshift',
    description: 'Charcoal suit, tie still knotted at four in the morning. Level 26.',
    accent: 0xa5b4fc,
    model: 'synty-suit',
    bodyColour: 0x6f7482,
    unlock: { kind: 'level', level: 26, gemCost: 2000 },
  },
  [SKIN.awaykit]: {
    id: SKIN.awaykit,
    name: 'Away Kit',
    description: 'Matchday navy, collar to boot. Still the wrong sport.',
    accent: 0xfde047,
    model: 'synty-striker',
    bodyColour: 0x5c6a91,
    unlock: { kind: 'purchase', gemCost: 1500 },
  },
};

/** Every skin, in catalogue order. */
export const SKIN_LIST: readonly SkinDefinition[] = Object.values(SKINS);

/**
 * Resolves a skin id to a definition, falling back to the default.
 *
 * Never throws. An id arrives from the wire, from a ticket, and from a database column, and in
 * all three cases the right answer to "id 97" is a player wearing Standard Issue — not a
 * disconnect, and not a crash in the render loop.
 */
export function skinById(id: number): SkinDefinition {
  return SKINS[id as SkinId] ?? (SKINS[SKIN.standard] as SkinDefinition);
}

/** True when `id` names a real skin. For validating input that will be stored. */
export function isKnownSkin(id: number): id is SkinId {
  return Object.prototype.hasOwnProperty.call(SKINS, id);
}

/** The item id under which ownership is recorded in core's inventory. */
export function skinItemId(id: SkinId): string {
  return `skin:${id}`;
}

/**
 * Whether a player meets a skin's requirement.
 *
 * Pure, and in `shared/` so the equip screen can grey out what you cannot have using exactly
 * the rule the server enforces — a screen that computes eligibility differently from the API
 * shows players buttons that fail.
 *
 * Purchase skins return false: meeting the price is not the same as having bought it, and
 * ownership for those is recorded in the inventory rather than derived. The caller checks
 * inventory first and only falls back to this.
 */
export function meetsUnlock(
  definition: SkinDefinition,
  progress: { level: number; rankPoints: number },
): boolean {
  switch (definition.unlock.kind) {
    case 'default':
      return true;
    case 'level':
      return progress.level >= definition.unlock.level;
    case 'rank':
      return progress.rankPoints >= definition.unlock.points;
    case 'purchase':
      return false;
  }
}

/** Human-readable requirement, for the equip screen. */
export function describeUnlock(unlock: SkinUnlock): string {
  switch (unlock.kind) {
    case 'default':
      return 'Owned';
    case 'level':
      return `Reach level ${unlock.level} · or ${unlock.gemCost} gems`;
    case 'rank':
      return `Earn ${unlock.points} rank points · or ${unlock.gemCost} gems`;
    case 'purchase':
      return `${unlock.gemCost} gems`;
  }
}

/**
 * What a skin costs in Gems, or null for the default kit.
 *
 * Every skin has a gem price (2026-09-09: all content is purchasable) — a level or a
 * rank gate is the free route to the same body, not the only route. `meetsUnlock` is the free
 * route; this is the paid one, and the shop offers whichever the player can take.
 */
export function skinGemCost(unlock: SkinUnlock): number | null {
  return unlock.kind === 'default' ? null : unlock.gemCost;
}
